import ProposalLogList from '@/proposal/components/ProposalLogList'
import { Box, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingStepThreadsGovernanceIcon } from 'src/icons'
import { ThreadApprovedProposal } from '@rolebase/shared/model/meetingProposals'

interface Props {
  proposals: ThreadApprovedProposal[]
}

// What the tension produced, under the tension itself, in a Governance step
// (REU-13).
//
// 🔑 The proposal is the unit, not a single "Governance changes" block: its own
// title is the heading, so two proposals decided one after the other stay
// distinguishable (REU13-D2/D5/D8). The heading borrows the small-caps label of
// "PROCHAINS PAS" so the card keeps one visual grammar — Yohan's own reference.
//
// ⚠️ Nothing is drawn when the tension produced no approved proposal at all:
// no empty heading under every unresolved tension, on the busiest screen of
// the app. The filtering happens upstream, in `readApprovedProposals`.
//
// An approved proposal that changed nothing DOES show, under its title, with a
// line saying so (REU14-D7/D10). It used to be hidden (REU13-D7), a rule taken
// back once the unit became the proposal: a decision was being taken and the
// screen said nothing at all. The line matters as much as the heading — a
// heading alone would read as a list that failed to load.
//
// The rows themselves come from ProposalLogList, already used by the proposal
// editor (REU13-D9): the wording, the links to roles, circles and members, and
// the eye that opens the diff of a modified role. The icon is the step's own
// (REU13-D12) — the row then says "this is governance" without a word.
export default function MeetingThreadGovernanceChanges({ proposals }: Props) {
  const { t } = useTranslation()

  if (proposals.length === 0) return null

  return (
    <VStack align="stretch" spacing={3} mt={3}>
      {proposals.map((proposal) => (
        <Box key={proposal.activityId}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            textTransform="uppercase"
            color="gray.500"
            _dark={{ color: 'gray.400' }}
            mb={1}
          >
            {proposal.title}
          </Text>

          {proposal.logs.length === 0 ? (
            <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }}>
              {t('MeetingThreadGovernanceChanges.noChanges')}
            </Text>
          ) : (
            <ProposalLogList
              logs={proposal.logs}
              readOnly
              icon={<MeetingStepThreadsGovernanceIcon size={16} />}
            />
          )}
        </Box>
      ))}
    </VStack>
  )
}
